import {
  ReactNode,
  useEffect,
  useMemo,
  useReducer,
  Dispatch as ReactDispatch,
} from 'react';
import { AnyAction } from '@reduxjs/toolkit';
import SubmittedProvider from '@paradigm/features/src/forms/submitted-context';
import { useStoredEntry } from '#/store/useStoredEntry';

import * as store from '#/unified-rfqs/ui/rfq-builder/store';
import {
  ActionsCtx,
  useCreateActions,
} from '#/unified-rfqs/ui/rfq-builder/actions';
import { createView, ViewCtx } from '#/unified-rfqs/ui/rfq-builder/view';
import usePrefillNotifier from '#/unified-rfqs/ui/rfq-builder/usePrefillNotifier';
import { useMultiProductVenues } from '#/feature-flags/context';
import usePremiumAdjustedDeltaHedge from '#/unified-rfqs/ui/rfq-builder/usePremiumAdjustedDeltaHedge';
import {
  useDrfqInstruments,
  useGrfqInstruments,
  useInstrumentsFetcher,
} from '#/unified-rfqs/ui/instruments/context';
import useHedgeLegPricePrefill from './useHedgeLegPricePrefill';

interface Props {
  readonly children: ReactNode;
  readonly prefill?: Partial<store.State>;
  readonly onPrefillError: (message: string) => void;
}

export function RfqBuilderProvider({
  children,
  prefill = {},
  onPrefillError,
}: Props) {
  const anonymousStored = useAnonymousStoredState();
  const { state, dispatch } = useRfqBuilderStore(anonymousStored, prefill);
  usePrefillNotifier({
    onError: onPrefillError,
    instruments: state.instruments,
    prefill,
  });

  useEffect(() => {
    anonymousStored.set(state.anonymous);
  }, [anonymousStored, state.anonymous]);

  const actions = useCreateActions(dispatch);
  const multiProductVenues = useMultiProductVenues();
  const view = createView(state, { multiProductVenues });

  useHedgeLegPricePrefill(state, actions);
  usePremiumAdjustedDeltaHedge({ state, greeks: view.greeks, dispatch });

  return (
    <SubmittedProvider>
      <ActionsCtx.Provider value={actions}>
        <ViewCtx.Provider value={view}>{children}</ViewCtx.Provider>
      </ActionsCtx.Provider>
    </SubmittedProvider>
  );
}

function useAnonymousStoredState() {
  const drfqAnonymousEntry = useStoredEntry<boolean>('um:drfq:anonymous');

  return useMemo(
    () => ({
      get: () => {
        const defaultValue = true;
        const value = drfqAnonymousEntry.get();
        if (value == null || typeof value !== 'string') return defaultValue;
        return JSON.parse(value) as boolean;
      },
      set: (value: boolean) => drfqAnonymousEntry.set(String(value)),
    }),
    [drfqAnonymousEntry],
  );
}

export function useRfqBuilderStore(
  anonymousStored: ReturnType<typeof useAnonymousStoredState>,
  prefill: Partial<store.State>,
): { state: store.State; dispatch: ReactDispatch<AnyAction> } {
  const drfqInstruments = useDrfqInstruments();
  const grfqInstruments = useGrfqInstruments();
  const initialVenue = prefill.venue ?? 'DBT';
  const initialAuction = prefill.auction ?? 'RFQ';
  const initialState = {
    ...store.INITIAL_STATE,
    anonymous: anonymousStored.get(),
    instruments:
      initialAuction === 'OB'
        ? grfqInstruments[initialVenue]
        : drfqInstruments[initialVenue],
    ...prefill,
  };
  const [state, dispatch] = useReducer(store.reducer, initialState);
  const { auction, venue } = state;
  const { fetch: fetchInstruments } = useInstrumentsFetcher();

  useEffect(() => {
    fetchInstruments(auction ?? 'RFQ', venue, (instruments) =>
      dispatch(store.updateInstruments(instruments)),
    );
    // Should run only auction and venue change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auction, venue]);

  return { state, dispatch };
}
